import React from 'react'
import { useAppSelector } from '../../../hooks/redux'
import { type RootState } from '../../../store/store'
import { generateDefaultValueFormField } from '../../../functions/agent/AgentModals'
import { regExps } from '../../../constants'
import DefaultInput from '../../../app/custom/inputs/DefaultInput'
import CustomSelect from '../../../app/custom/selects/CustomSelect'
import GroupInput from '../../../app/custom/inputs/GroupInput'
import CustomSwitch from '../../../app/custom/inputs/CustomSwitch'
import CustomDynamicStatuses from '../../../app/custom/selects/CustomDynamicStatuses'

export default function AdditionalOptionsFields({
    setValue,
    errors,
    register,
    control,
    unitID, objectID, modal
}: any): JSX.Element {
    const modalID = modal?.[0]?.id
    const units = useAppSelector((state: RootState) => state.units.units)
    const newPropertyFields = useAppSelector((state: RootState) => state.property.newPropertyFields)
    const config = useAppSelector((state: RootState) => state.app.config)
    const configSelectors = config?.units
    const currentUnit = units?.filter((el: any) => el.id === Number(unitID))[0]
    const marketingData = currentUnit?.attributes?.Marketing
    const propertyData = currentUnit?.attributes?.Property?.data

    const isDisabled = false // modalID === "save_after_draft_agent_unit"

    const rowNumber = {
        id: 'Number',
        content: ['Unit number', 'Input unit number'],
        minLength: 1, element: 'input', required: false, description: 'This field is optional, but completing it will enhance the credibility of your listing.',
        ...(generateDefaultValueFormField(currentUnit?.attributes?.Number))
    }
    const rowBedrooms = {
        id: 'Bedrooms',
        content: ['Bedrooms', 'Input count of beds or "Studio"'],
        minLength: 1, element: 'input', required: true, disabled: isDisabled,
        ...(generateDefaultValueFormField(currentUnit?.attributes?.Bedrooms)), regexp: regExps.forBedrooms
    }
    const rowBathrooms = {
        id: 'Bathrooms',
        content: ['Bathrooms', 'Input count of baths'],
        minLength: 1, element: 'input', required: true,
        ...(generateDefaultValueFormField(currentUnit?.attributes?.Bathrooms)), regexp: regExps.isNumber
    }
    const rowGuestBathroom = {
        id: 'GuestBathroom',
        content: ['Guest Bathroom', 'Unit with guest bathroom ?'],
        selectors: ['YES', 'NO'],
        defaultValue: currentUnit?.attributes?.GuestBathroom, element: 'input-switch', required: true
    }
    const rowMaidsRoom = {
        id: 'MaidsRoom',
        content: ['Maids Room', 'Unit with maids room ?'],
        selectors: ['YES', 'NO'],
        element: 'input-switch', defaultValue: currentUnit?.attributes?.MaidsRoom, required: true
    }
    const rowFeatures = {
        id: 'Features',
        content: ['Property Features', 'Select features'],
        selectors: configSelectors?.Features, element: 'select', multiple: true, required: false,
        ...(generateDefaultValueFormField(currentUnit?.attributes?.Features))
    }
    const rowAmenities = {
        id: 'Amenities',
        content: ['Property Amenities', 'Select amenities'],
        element: 'select', multiple: true, selectors: configSelectors?.Amenities,
        ...(generateDefaultValueFormField(currentUnit?.attributes?.Amenities))
    }
    const rowAppliances = {
        id: 'Appliances',
        content: ['Property Appliances', 'Select appliances'],
        element: 'select', multiple: true, selectors: configSelectors?.Appliances,
        ...(generateDefaultValueFormField(currentUnit?.attributes?.Appliances))
    }
    const rowFit = {
        id: 'Fit',
        content: ['Property Fit', 'Select fit'],
        element: 'select', selectors: configSelectors?.Fit,
        ...(generateDefaultValueFormField(currentUnit?.attributes?.Fit))
    }
    const rowView = {
        id: 'View',
        content: ['Property view', 'Select view'],
        element: 'select', selectors: configSelectors?.View,
        ...(generateDefaultValueFormField(currentUnit?.attributes?.View))
    }

    const rowOccupied = { id: 'dynamicStatuses', content: ['Occupied until', 'Select date'], required: true }

    return (
        <>
            <div className="text-center py-4 my-2">
                <h1 className="text-dark m-0 fs-3">
                    Additional options fields
                </h1>
            </div>
            <CustomDynamicStatuses setValue={setValue} row={rowOccupied} register={register} errors={errors} control={control} />
            <DefaultInput row={rowNumber} register={register} errors={errors} setValue={setValue} />
            <DefaultInput row={rowBedrooms} register={register} errors={errors} setValue={setValue} />
            <DefaultInput row={rowBathrooms} register={register} errors={errors} setValue={setValue} />
            <CustomSwitch row={rowGuestBathroom} errors={errors} register={register} control={control} />
            <CustomSwitch row={rowMaidsRoom} errors={errors} register={register} control={control} />
            <CustomSelect data={rowFeatures} control={control} register={register} setValue={setValue} errors={errors} />
            <CustomSelect data={rowAmenities} control={control} register={register} setValue={setValue} errors={errors} />
            <CustomSelect data={rowAppliances} control={control} register={register} setValue={setValue} errors={errors} />
            <CustomSelect data={rowFit} control={control} register={register} setValue={setValue} errors={errors} />
            <CustomSelect data={rowView} control={control} register={register} setValue={setValue} errors={errors} />
        </>
    )
}
